import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { MapPin, Loader2, Search, CheckCircle2 } from 'lucide-react';

import { API_BASE } from '@/lib/api';
import { Input, Textarea } from '@/components/ui/input';

interface Addr { formatted?: string; lat?: number; lng?: number }

const parse = (v: string): Addr => { try { return v ? (JSON.parse(v) as Addr) : {}; } catch { return {}; } };

/**
 * Address field for survey fill. When GPS coordinates are present (captured at
 * survey start) it reverse-geocodes nearby addresses so the surveyor can pick
 * the closest match. As the surveyor types, it also forward-geocodes the text
 * for live autocomplete suggestions — while still allowing free manual entry.
 * Stores `{ formatted, lat, lng }` JSON.
 */
export const GeoAddressField: React.FC<{ value: string; onChange: (v: string) => void; testId: string; mode?: string }> = ({ value, onChange, testId, mode = 'address_geo' }) => {
  const cur = parse(value);
  const hasCoords = typeof cur.lat === 'number' && typeof cur.lng === 'number';
  const showAddress = mode !== 'geo_only';
  const showCoords = mode !== 'address_only';
  const [candidates, setCandidates] = useState<string[]>([]);
  const [results, setResults] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [searching, setSearching] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  const [fetchedKey, setFetchedKey] = useState<string | null>(null);
  const debounce = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!hasCoords) return;
    const key = `${cur.lat},${cur.lng}`;
    if (fetchedKey === key) return;
    setFetchedKey(key);
    setLoading(true);
    axios.get<{ candidates: string[] }>(`${API_BASE}/public/settings/geocode/reverse`, { params: { lat: cur.lat, lng: cur.lng } })
      .then((r) => {
        const c = r.data?.candidates || [];
        setCandidates(c);
        if (c.length && !cur.formatted) onChange(JSON.stringify({ ...cur, formatted: c[0] }));
      })
      .catch(() => setCandidates([]))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cur.lat, cur.lng]);

  const set = (patch: Partial<Addr>): void => onChange(JSON.stringify({ ...cur, ...patch }));

  const runSearch = (text: string): void => {
    if (debounce.current) clearTimeout(debounce.current);
    if (!text || text.trim().length < 3) { setResults([]); return; }
    debounce.current = setTimeout(() => {
      setSearching(true);
      axios.get<{ candidates: string[] }>(`${API_BASE}/public/settings/geocode/search`, { params: { q: text } })
        .then((r) => setResults((r.data?.candidates || []).filter((c) => c !== text)))
        .catch(() => setResults([]))
        .finally(() => setSearching(false));
    }, 350);
  };

  const onType = (text: string): void => { set({ formatted: text }); runSearch(text); };
  const pick = (c: string): void => { setResults([]); setCollapsed(true); set({ formatted: c }); };

  return (
    <div className="grid gap-3 rounded-xl border border-border bg-raptor-neutral/30 p-3" data-testid={testId}>
      {showCoords && hasCoords && (
        <div className="flex items-center gap-2 text-xs font-medium text-raptor-navy/70">
          <span className="flex h-6 w-6 items-center justify-center rounded-full bg-raptor-blue/10 shrink-0">
            <MapPin size={13} className="text-raptor-blue" />
          </span>
          <span>GPS captured</span>
          <span className="font-tabular text-raptor-navy/90">{cur.lat}, {cur.lng}</span>
        </div>
      )}

      {showAddress && loading && (
        <div className="flex items-center gap-2 text-xs text-raptor-navy/50">
          <Loader2 size={12} className="animate-spin" /> Finding nearby addresses…
        </div>
      )}

      {showAddress && candidates.length > 0 && (
        <div className="grid gap-1.5">
          <span className="text-[11px] font-semibold uppercase tracking-wide text-raptor-navy/50">Closest to your GPS</span>
          {collapsed ? (
            <button type="button" onClick={() => setCollapsed(false)} data-testid={`${testId}-selected`}
              className="flex items-center gap-2 text-left text-sm px-3 py-2 rounded-lg border bg-raptor-blue/10 border-raptor-blue text-raptor-navy font-medium">
              <CheckCircle2 size={15} className="text-raptor-blue shrink-0" />
              <span className="flex-1">{cur.formatted}</span>
              <span className="text-xs font-semibold text-raptor-blue underline shrink-0" data-testid={`${testId}-change`}>Change</span>
            </button>
          ) : (
            <div className="grid gap-1">
              {candidates.map((c, i) => (
                <button key={i} type="button" onClick={() => pick(c)} data-testid={`${testId}-cand-${i}`}
                  className={`text-left text-sm px-3 py-2 rounded-lg border transition-all ${cur.formatted === c ? 'bg-raptor-blue/10 border-raptor-blue text-raptor-navy font-medium' : 'bg-white border-border hover:border-raptor-blue/40 hover:bg-white text-raptor-navy/80'}`}>
                  {c}
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {showAddress && (
      <div className="grid gap-1.5">
        <label className="text-[11px] font-semibold uppercase tracking-wide text-raptor-navy/50">Physical address</label>
        <div className="relative">
          <Textarea rows={2} placeholder="Start typing an address, or confirm the match above"
            value={cur.formatted || ''} onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => onType(e.target.value)} data-testid={`${testId}-manual`} />
          {searching && (
            <span className="absolute right-2 top-2 text-raptor-navy/40"><Loader2 size={13} className="animate-spin" /></span>
          )}
          {results.length > 0 && (
            <div className="absolute z-20 mt-1 w-full overflow-hidden rounded-lg border border-border bg-white shadow-lg" data-testid={`${testId}-suggestions`}>
              <div className="flex items-center gap-1.5 border-b border-border/60 px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wide text-raptor-navy/40">
                <Search size={11} /> Suggestions
              </div>
              {results.map((c, i) => (
                <button key={i} type="button" onClick={() => pick(c)} data-testid={`${testId}-sugg-${i}`}
                  className="block w-full text-left text-sm px-3 py-2 text-raptor-navy/80 hover:bg-raptor-blue/5">
                  {c}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
      )}

      {showCoords && !hasCoords && (
        <div className="grid grid-cols-2 gap-2">
          <Input type="number" step="any" placeholder="Latitude (optional)"
            value={cur.lat ?? ''} onChange={(e: React.ChangeEvent<HTMLInputElement>) => set({ lat: e.target.value === '' ? undefined : Number(e.target.value) })} className="font-tabular text-xs" />
          <Input type="number" step="any" placeholder="Longitude (optional)"
            value={cur.lng ?? ''} onChange={(e: React.ChangeEvent<HTMLInputElement>) => set({ lng: e.target.value === '' ? undefined : Number(e.target.value) })} className="font-tabular text-xs" />
        </div>
      )}
    </div>
  );
};

export default GeoAddressField;
